import React, { useEffect, useState } from "react";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Save, Building2, Mail, Phone, MapPin, Clock, Instagram, Facebook, Coins, Image as ImageIcon, ShieldCheck } from "lucide-react";
import { adminApi, formatErr } from "@/admin/api";
import { Panel, AdminInput, AdminTextarea } from "@/admin/components/ui";

export default function AdminSettings() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    adminApi.get("/admin/settings").then((r) => setForm(r.data)).finally(() => setLoading(false));
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setBusy(true);
    try {
      const { business_name, contact_email, contact_phone, whatsapp, location, hours, instagram_url, facebook_url, default_credit_value, notes } = form;
      await adminApi.put("/admin/settings", { business_name, contact_email, contact_phone, whatsapp, location, hours, instagram_url, facebook_url, default_credit_value: default_credit_value ? Number(default_credit_value) : undefined, notes });
      toast.success("Settings saved.");
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  if (loading || !form) return <p className="text-sm text-white/40">Loading…</p>;

  return (
    <div data-testid="page-admin-settings" className="space-y-6 max-w-5xl">
      <SEO title="AdminSettings · WelkomBiz Photography" noindex />
      <Panel title="Business Information" testId="settings-business">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AdminInput data-testid="settings-business-name" label="Business Name" icon={Building2} value={form.business_name || ""} onChange={upd("business_name")} />
          <AdminInput data-testid="settings-hours" label="Hours" icon={Clock} value={form.hours || ""} onChange={upd("hours")} />
          <AdminInput data-testid="settings-location" label="Location" icon={MapPin} value={form.location || ""} onChange={upd("location")} className="md:col-span-2" />
        </div>
      </Panel>

      <Panel title="Contact Details" testId="settings-contact">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AdminInput data-testid="settings-email" label="Email" icon={Mail} type="email" value={form.contact_email || ""} onChange={upd("contact_email")} />
          <AdminInput data-testid="settings-phone" label="Phone" icon={Phone} value={form.contact_phone || ""} onChange={upd("contact_phone")} />
          <AdminInput data-testid="settings-whatsapp" label="WhatsApp" icon={Phone} value={form.whatsapp || ""} onChange={upd("whatsapp")} />
        </div>
      </Panel>

      <Panel title="Social Media" testId="settings-social">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AdminInput data-testid="settings-instagram" label="Instagram URL" icon={Instagram} value={form.instagram_url || ""} onChange={upd("instagram_url")} placeholder="https://instagram.com/..." />
          <AdminInput data-testid="settings-facebook" label="Facebook URL" icon={Facebook} value={form.facebook_url || ""} onChange={upd("facebook_url")} placeholder="https://facebook.com/..." />
        </div>
      </Panel>

      <Panel title="Credits & Pricing" testId="settings-credits">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AdminInput data-testid="settings-credit-value" label="Default Credit Value (ZAR)" icon={Coins} type="number" min={0} value={form.default_credit_value ?? ""} onChange={upd("default_credit_value")} />
        </div>
        <p className="text-[11px] text-white/45 mt-3 font-body">Real credit deduction & pricing will fully activate in Phase 3.</p>
      </Panel>

      <Panel title="Storage & Watermark (Coming in Phase 3)" testId="settings-storage">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 opacity-60">
          <AdminInput label="Storage Provider" icon={ImageIcon} value="Cloudflare R2 (pending)" disabled />
          <AdminInput label="Watermark Style" icon={ShieldCheck} value="WelkomBiz Default (pending)" disabled />
        </div>
      </Panel>

      <Panel title="Admin Users (Coming in Phase 3)" testId="settings-admin-users">
        <p className="text-sm text-white/55 font-body">
          Additional admin user invitations and role-based permissions will be available in a future phase. For now, a single seeded admin account is active.
        </p>
      </Panel>

      <Panel title="Internal Notes" testId="settings-notes">
        <AdminTextarea data-testid="settings-notes-textarea" rows={4} value={form.notes || ""} onChange={upd("notes")} placeholder="Anything the team should remember." />
      </Panel>

      <div className="flex justify-end">
        <button data-testid="settings-save" disabled={busy} onClick={save} className="btn-primary !py-2.5 !px-5 !text-[11px] disabled:opacity-60">
          <Save className="w-3.5 h-3.5" /> Save Settings
        </button>
      </div>
    </div>
  );
}
